import { Box, Button, IconButton } from "@mui/joy";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useMemo, useState } from "react";

export interface PaginationProps {
    /**
     * Total number of pages
     */
    count: number;
    /**
     * The current page (controlled)
     */
    page?: number;
    /**
     * The initial page (uncontrolled)
     */
    defaultPage?: number;
    /**
     * Callback fired when the page is changed
     */
    onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
    /**
     * The variant to use
     */
    variant?: "outlined" | "plain" | "soft" | "solid";
    /**
     * The shape of the buttons
     */
    shape?: "rounded" | "circular";
    /**
     * The size of the component
     */
    size?: "sm" | "md" | "lg";
    /**
     * If true, disable the component
     */
    disabled?: boolean;
}

export function Pagination({
    count,
    page: controlledPage,
    defaultPage = 1,
    onChange,
    variant = "outlined",
    shape = "rounded",
    size = "md",
    disabled = false,
}: PaginationProps) {
    const [internalPage, setInternalPage] = useState(defaultPage);
    const isControlled = controlledPage !== undefined;
    const currentPage = isControlled ? controlledPage : internalPage;

    const handlePageChange = (newPage: number) => {
        if (disabled || newPage < 1 || newPage > count) return;

        if (!isControlled) {
            setInternalPage(newPage);
        }

        if (onChange) {
            const event = {
                target: { value: newPage },
                currentTarget: { value: newPage },
            } as unknown as React.ChangeEvent<unknown>;
            onChange(event, newPage);
        }
    };

    const pageNumbers = useMemo(() => {
        const pages: (number | string)[] = [];
        const maxVisible = 7; // Show up to 7 page numbers

        if (count <= maxVisible) {
            // Show all pages if count is small
            for (let i = 1; i <= count; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(count - 1, currentPage + 1);

            // Adjust if we're near the start
            if (currentPage <= 3) {
                endPage = Math.min(5, count - 1);
            }

            // Adjust if we're near the end
            if (currentPage >= count - 2) {
                startPage = Math.max(2, count - 4);
            }

            // Add ellipsis and pages
            if (startPage > 2) {
                pages.push("ellipsis-start");
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < count - 1) {
                pages.push("ellipsis-end");
            }

            // Always show last page
            if (count > 1) {
                pages.push(count);
            }
        }

        return pages;
    }, [count, currentPage]);

    const getButtonSx = () => {
        const baseSx: any = {
            minWidth: size === "sm" ? "32px" : size === "md" ? "40px" : "48px",
            height: size === "sm" ? "32px" : size === "md" ? "40px" : "48px",
        };

        if (shape === "circular") {
            baseSx.borderRadius = "50%";
        } else if (shape === "rounded") {
            baseSx.borderRadius = "var(--std-border-radius, 8px)";
        }

        return baseSx;
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexWrap: "wrap",
            }}
        >
            <IconButton
                variant={"plain"}
                size={size}
                disabled={disabled || currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                sx={getButtonSx()}
            >
                <ChevronLeft size={size === "sm" ? 16 : size === "md" ? 18 : 20} />
            </IconButton>

            {pageNumbers.map((page, index) => {
                if (typeof page === "string") {
                    return (
                        <Box
                            key={`${page}-${index}`}
                            sx={{
                                minWidth: size === "sm" ? "32px" : size === "md" ? "40px" : "48px",
                                height: size === "sm" ? "32px" : size === "md" ? "40px" : "48px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            ...
                        </Box>
                    );
                }

                const isSelected = page === currentPage;

                return (
                    <IconButton
                        key={page}
                        variant={isSelected ? "outlined" : variant}
                        size={size}
                        disabled={disabled}
                        onClick={() => handlePageChange(page)}
                        sx={getButtonSx()}
                    >
                        {page}
                    </IconButton>
                );
            })}

            <IconButton
                variant={"plain"}
                size={size}
                disabled={disabled || currentPage === count}
                onClick={() => handlePageChange(currentPage + 1)}
                sx={getButtonSx()}
            >
                <ChevronRight size={size === "sm" ? 16 : size === "md" ? 18 : 20} />
            </IconButton>
        </Box>
    );
}
